import { CalendarApiItem, CalendarSlotItem } from 'types/calendar'
import { ServiceItem, ServiceApiItem, ServiceApiTypeEnum, ServiceMemberPriceType, ServiceDiscountTypes } from 'types/service'
import { DayOfWeek, GroupClass, GroupClassApi, GroupClassFormItem, ModalDataAttributes, RecurrenceSchedule, UserModel } from 'components/Modals/types'

export function denormalizeService(s: ServiceApiItem): ServiceItem {
    const {
        id,
        accentColorHex: accent_color,
        primaryColorHex: primary_color,
        secondaryColorHex: secondary_color,
        duration,
        addons,
        blockTimeAfter: blocked_to,
        blockTimeBefore: blocked_from,
        category,
        name,
        price,
        maxCapacity: max_capacity,
        description,
        longDescription: long_description,
        customSlug: slug,
        imageUrl: image_url,
        staff,
        memberPrice: member_price,
        memberPriceType: member_price_type,
        preventDuplicateBookingByEmail: prevent_duplicate_booking_by_email,
        preventDuplicateBookingByPhone: prevent_duplicate_booking_by_phone,
        deletedAt
    } = s

    return {
        id,
        accent_color,
        primary_color,
        secondary_color,
        duration,
        addons,
        is_public: s.public || false,
        max_participants: max_capacity || 0,
        blocked_from,
        blocked_to,
        category,
        name,
        price,
        description,
        long_description,
        service_type: s.type as ServiceApiTypeEnum,
        slug,
        staff:
            (staff &&
                staff.map(
                    ({ id, user }: { id: string; user: UserModel }) => ({
                        id,
                        user_id: user.id as string,
                        first_name: user.firstName,
                        last_name: user.lastName,
                        staff_image: user.imageUrl
                    })
                )) ||
            [],
        image_url,
        member_price,
        member_price_type,
        prevent_duplicate_booking_by_email,
        prevent_duplicate_booking_by_phone,
        is_archived: !!deletedAt 
    }
}

export function denormalizeGroupClass(
    gc: CalendarApiItem,
    idx: number
): CalendarSlotItem | void {
    const {
        duration,
        groupClassAvailabilityId: availability_id,
        groupClassId: id,
        groupClassSettingId: setting_id,
        locationId: location_id,
        locationName: location_name,
        locationSpaceName: space_name,
        locationTimezone: timezone,
        price,
        primaryColorHex: primary_hex,
        serviceId: service_id,
        serviceName: service_name,
        staffId: staff_id,
        staffName: staff_name,
        imageUrl,
        serviceType: service_type,
        groupClassName: group_class_name
    } = gc

    const [year, month, day] = gc.date.split('-') as unknown[] as number[]
    const [from_hour, from_minute] = gc.startTime.split(
        ':'
    ) as unknown[] as number[]
    const [to_hour, to_minute] = gc.endTime.split(
        ':'
    ) as unknown[] as number[]
    const start = new Date(year, month - 1, day, from_hour, from_minute, 0)
    const end = new Date(year, month - 1, day, to_hour, to_minute, 0)
    if (to_hour < from_hour) {
        end.setDate(start.getDate() + 1)
    }

    if (id) {
        return {
            id,
            availability_id,
            setting_id,
            staff_id,
            staff_name,
            slots: {
                available: gc.slotsAvailable,
                taken: gc.slotsTaken,
                max: gc.maxCapacity
            },
            title: service_name,
            duration,
            instructor_name: staff_name,
            location_id,
            location_name,
            price,
            service_id,
            service_name,
            space_name,
            timezone,
            end,
            start,
            primary_hex,
            imageUrl: imageUrl ? imageUrl : '',
            service_type,
            group_class_name
        }
    }
}

export function mapGroupClasses(cached_group_classes: GroupClassApi[], services: ServiceItem[]): Record<string, GroupClassFormItem[]> {
    const already_added: string[] = []
    let ret: Record<string, GroupClassFormItem[]> = {}
    cached_group_classes.forEach((item: GroupClassApi) => {
        if (
            item &&
            item.id &&
            already_added.indexOf(item.id) == -1
        ) {
            const service = services.find(
                (service) => service.id == item.serviceId
            )
            if (service) {
                let rec: GroupClass
                if (
                    item.serviceId &&
                    service &&
                    service.service_type ===
                    ServiceApiTypeEnum.SERIES
                ) {
                    const {
                        id: setting_id,
                        locationId: group_location_id,
                        staffId: group_staff_id,
                        locationSpaceId: group_location_space_id,
                    } = item.groupClassSetting as unknown as Record<
                        string,
                        string
                    >
                    rec = {
                        id: item.id,
                        date: new Date(
                            item?.groupClassAvailabilities?.[0]
                                ?.date as string
                        ),
                        location: group_location_id,
                        location_space: group_location_space_id,
                        staff: group_staff_id,
                        is_recurring: false,
                        time: item?.groupClassAvailabilities?.[0]
                            ?.startTime as string,
                        duration: service.duration,
                        name: item?.name,
                        service_type: service.service_type,
                        setting_id,
                        availabilities:
                            item.groupClassAvailabilities?.map(
                                (ava) => {
                                    return {
                                        id: ava.id,
                                        date: new Date(ava.date),
                                        start_time: ava.startTime,
                                        end_time: ava.endTime,
                                    }
                                }
                            ) || [],
                    }
                } else {
                    const {
                        dayOfWeek,
                        effectiveDate,
                        startTime,
                        expiryDate,
                        recurring,
                    } = item
                    const {
                        id: setting_id,
                        locationId: group_location_id,
                        staffId: group_staff_id,
                        locationSpaceId: group_location_space_id,
                    } = item.groupClassSetting as unknown as Record<
                        string,
                        string
                    >
                    rec = {
                        id: item.id,
                        date: new Date(effectiveDate as string),
                        location: group_location_id,
                        location_space: group_location_space_id,
                        staff: group_staff_id,
                        is_recurring:
                            recurring as unknown as boolean,
                        time: (startTime as string)?.substring(
                            0,
                            5
                        ),
                        service_type: service.service_type,
                        setting_id,
                    }
                    if (expiryDate) {
                        rec.ends_at = new Date(expiryDate)
                    }
                    const [hours, minutes] = (startTime as string)
                        .substring(0, 5)
                        .split(':')
                    rec.date.setHours(parseInt(hours))
                    rec.date.setMinutes(parseInt(minutes))
                    if (recurring) {
                        rec.day_of_week =
                            dayOfWeek as unknown as DayOfWeek
                        rec.repeats =
                            item.recurrenceSchedule as unknown as RecurrenceSchedule
                    }
                }

                if (item.serviceId) {
                    if (!ret[item.serviceId]) {
                        ret[item.serviceId] = []
                    }
                    ret[item.serviceId].push({
                        ...rec,
                        _original: rec,
                    })
                }
                already_added.push(item.id)
            }
        }
    })
    return ret
}

export function mapServiceToAttributes({
    id,
    name,
    category,
    description,
    duration,
    is_public,
    max_participants,
    price,
    primary_color,
    service_type,
    image_url,
    staff,
    blocked_from,
    blocked_to,
    member_price,
    member_price_type,
    prevent_duplicate_booking_by_email,
    prevent_duplicate_booking_by_phone
}: ServiceItem, index: number, group_classes: GroupClassFormItem[], attributes?: ModalDataAttributes, activeTab?: number): ModalDataAttributes {
    const discount_index = member_price_type
        ? Object.keys(ServiceMemberPriceType).indexOf(member_price_type)
        : -1
    return {
        ...attributes,
        id,
        name,
        activeTab: activeTab || 0,
        category: category.id,
        description: description || '',
        duration: duration || '',
        is_public,
        image_url: image_url || '',
        max_participants: max_participants || '',
        price: price || '',
        primary_color: primary_color || '',
        service_type,
        list_item_idx: index,
        group_classes: group_classes || [],
        staff_assigned: staff,
        blocked_from: blocked_from || 0,
        blocked_to: blocked_to || 0,
        show_block: !!blocked_from && !!blocked_to,
        has_member_price: !!member_price && discount_index != -1,
        member_price: member_price || 0,
        member_price_type: discount_index != -1 ? ServiceDiscountTypes[discount_index] : ServiceDiscountTypes[0],
        prevent_duplicate_booking_by_email: prevent_duplicate_booking_by_email as boolean,
        prevent_duplicate_booking_by_phone: prevent_duplicate_booking_by_phone as boolean
    }
}
